import { useMemo } from 'react';
import { useConceptAngles } from '@/features/design/concept';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import { useRendersForConcept } from '@/features/design/render/model/renders-cache';
import { RenderStatus } from '@/shared/constants';
import { isUserVisibleRenderType } from '@/types/render';

/**
 * Resolve the render id per concept-angle that the estimate should be generated
 * from — the user's selected render for each angle (falling back to the latest
 * completed render). Mirrors `AlbumTabContent.selectedRendersForAlbum` so an
 * estimate covers the same renders an album would. Returns a flat id list ready
 * for the `selected_renders` request field.
 */
export function useConceptSelectedRenders(conceptId: string): string[] {
  const { data: conceptAnglesResponse } = useConceptAngles(conceptId);
  const conceptAngles = conceptAnglesResponse?.data;
  const rendersForConcept = useRendersForConcept(conceptId);
  const selectedRenderIds = useConceptStore(
    (state) => state.ui.selectedRenderId
  );

  return useMemo(() => {
    if (!conceptAngles) return [];
    const result: string[] = [];
    conceptAngles.forEach((ca) => {
      const completed = rendersForConcept
        .filter(
          (r) =>
            r.relationships?.concept_angle?.data?.id === ca.id &&
            r.attributes.status === RenderStatus.COMPLETED &&
            isUserVisibleRenderType(r.attributes.render_type)
        )
        .sort(
          (a, b) =>
            new Date(a.attributes.created_at).getTime() -
            new Date(b.attributes.created_at).getTime()
        );
      if (completed.length === 0) return;
      const selectedId = selectedRenderIds[ca.id];
      const chosen =
        (selectedId && completed.find((r) => r.id === selectedId)) ||
        completed[completed.length - 1];
      if (chosen) result.push(chosen.id);
    });
    return result;
  }, [conceptAngles, rendersForConcept, selectedRenderIds]);
}
